import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight, Truck, ShieldCheck, Clock, Recycle, Star, MapPin, Phone, Package, Plus, Minus, CircleCheck } from "lucide-react";
import { IDF_CITIES } from "@/data/cities";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { optimizeImage, optimizeImageSrcSet } from "@/lib/images";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";
import StickyMobileMoverCTA from "@/components/StickyMobileMoverCTA";

const Home = () => {
  useDocumentTitle(
    "cartonmove — Cartons de déménagement livrés en Île-de-France",
    "Achetez vos cartons et fournitures de déménagement en ligne. Livraison rapide partout en Île-de-France. Cartons standards, renforcés, spéciaux verres et penderies."
  );
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroIndex, setHeroIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [heroQty, setHeroQty] = useState(1);
  const { addItem, setOpen } = useCart();

  useEffect(() => {
    fetchProducts()
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const featured = products.filter((p) => p.featured).slice(0, 8);
  // Liste fixe de produits pour le carrousel hero (ordre maîtrisé)
  const HERO_SLUGS = [
    "carton-standard-renforce",
    "carton-75-verres",
    "carton-penderie-petit",
    "carton-12-bouteilles",
  ];
  const heroProducts = HERO_SLUGS.map((s) => products.find((p) => p.slug === s)).filter(Boolean);
  const heroProduct = heroProducts[heroIndex] || null;

  // Reset quantity when product changes
  useEffect(() => {
    setHeroQty(1);
  }, [heroIndex]);

  // Auto-rotate hero every 3s (pause on hover)
  useEffect(() => {
    if (heroProducts.length < 2 || isPaused) return;
    const id = setInterval(() => {
      setHeroIndex((i) => (i + 1) % heroProducts.length);
    }, 3000);
    return () => clearInterval(id);
  }, [heroProducts.length, isPaused]);

  const handleHeroAdd = () => {
    if (!heroProduct) return;
    addItem(heroProduct, 1);
    toast.success(`${heroProduct.name} ajouté au panier`, {
      action: { label: "Voir le panier", onClick: () => setOpen(true) },
    });
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-[#EEF3F7] overflow-hidden">
        <div className="container-cdm pt-6 pb-12 md:pt-10 md:pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-8 fade-up">
            <div className="inline-flex items-center gap-2 border border-[#cfdbe6] bg-white px-3 py-1.5 mb-6 rounded-full">
              <span className="h-2 w-2 bg-[#237cb5] rounded-full animate-pulse"></span>
              <span className="overline">Livraison express en Île-de-France</span>
            </div>
            <h1 className="font-display font-extrabold text-[28px] sm:text-[33px] md:text-[48px] leading-[1.02] tracking-tight">
              Vos cartons de <span className="text-[#237cb5]">déménagement</span>, <br />
              livrés chez vous.
            </h1>
            <p className="mt-8 text-base sm:text-lg text-[#3a4a5a] max-w-xl leading-[1.7]">
              Cartons standards, renforcés, spéciaux verres, vaisselle, penderie… Toutes les
              fournitures dont vous avez besoin pour un déménagement réussi, livrées partout en
              Île-de-France.
            </p>
          </div>

          <div className="lg:col-span-4 fade-up delay-200 flex justify-center lg:justify-end">
            <div
              className="relative aspect-[4/5] sm:aspect-[5/6] w-full max-w-[480px] bg-[#F3EFE9] overflow-hidden rounded-3xl shadow-[0_30px_60px_-20px_rgba(15,29,43,0.35)]"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              data-testid="hero-carousel"
            >
              {/* Slides — alignées vers le haut pour voir tout le carton */}
              {heroProducts.map((p, i) => (
                <img
                  key={p.id}
                  src={optimizeImage(p.image, 800)}
                  srcSet={optimizeImageSrcSet(p.image, [320, 480, 640, 800])}
                  sizes="(max-width: 640px) 90vw, 400px"
                  alt={p.name}
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  fetchpriority={i === 0 ? "high" : "auto"}
                  className={`absolute inset-x-0 top-0 w-full h-[68%] object-contain object-top p-4 sm:p-5 transition-opacity duration-700 ease-out ${i === heroIndex ? "opacity-100" : "opacity-0"}`}
                />
              ))}

              {/* Soft gradient at the bottom for readability */}
              <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/45 via-black/10 to-transparent pointer-events-none" />

              {/* Indicator dots */}
              {heroProducts.length > 1 && (
                <div className="absolute top-4 right-4 flex items-center gap-1.5 z-10" data-testid="hero-indicators">
                  {heroProducts.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setHeroIndex(i)}
                      aria-label={`Produit ${i + 1}`}
                      data-testid={`hero-indicator-${i}`}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === heroIndex ? "w-7 bg-[#237cb5]" : "w-1.5 bg-[#237cb5]/40 hover:bg-[#237cb5]/70"
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* Overlay product card — compact, bas de la photo */}
              {heroProduct && (
                <div
                  key={heroProduct.id}
                  className="absolute bottom-4 left-4 right-4 rounded-2xl bg-white/95 backdrop-blur-md border border-white p-4 shadow-2xl fade-up"
                  data-testid="hero-product-card"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#237cb5]/10 text-[#237cb5] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em]">
                      <Star className="h-3 w-3 fill-[#237cb5]" /> Meilleure vente
                    </span>
                  </div>
                  <h3
                    className="mt-2.5 text-[15px] font-extrabold text-[#0f1d2b] leading-tight line-clamp-1"
                    data-testid="hero-product-name"
                    title={heroProduct.name}
                  >
                    {heroProduct.name}
                  </h3>

                  <div className="mt-3 flex items-center justify-between gap-2">
                    <div className="flex items-baseline gap-1.5" data-testid="hero-product-price">
                      <span className="font-extrabold text-[20px] text-[#0f1d2b] leading-none">
                        {heroProduct.price.toFixed(2).replace(".", ",")} €
                      </span>
                      <span className="text-[11px] text-[#5b6b7a]">/ unité</span>
                    </div>
                    <Link
                      to={`/produit/${heroProduct.slug}`}
                      className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#FF7A00] hover:bg-[#E66E00] text-white text-[12.5px] font-semibold px-4 h-9 transition-colors shadow-[0_14px_24px_-10px_rgba(255,122,0,0.55)]"
                      data-testid="hero-add-to-cart"
                    >
                      <Plus className="h-3.5 w-3.5" /> Acheter
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS - 4 atouts */}
      <section className="bg-white border-y border-[#e6edf3]" id="livraison">
        <div className="container-cdm py-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#e6edf3]">
          {[
            { icon: Truck, title: "Livraison 24-48h", desc: "Partout en IDF" },
            { icon: ShieldCheck, title: "Qualité pro", desc: "Cartons renforcés" },
            { icon: Clock, title: "Commande facile", desc: "En quelques clics" },
            { icon: Recycle, title: "100% recyclable", desc: "Carton kraft écologique" },
          ].map(({ icon: Icon, title, desc }, i) => (
            <div key={i} className="px-4 md:px-8 first:pl-0 last:pr-0 fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <Icon className="h-6 w-6 text-[#237cb5] mb-3" />
              <div className="font-display font-bold text-sm text-[#0f1d2b]">{title}</div>
              <div className="text-xs text-[#5b6b7a] mt-1">{desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SEO INTRO + 4 KEY PRODUCTS — Paris & Île-de-France */}
      <section id="produits" className="section-pad bg-white border-t border-[#e6edf3]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline">Boutique cartonmove</div>
            <h2 className="mt-3 text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Cartons de déménagement à Paris et en Île-de-France
            </h2>
            <p className="mt-5 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Cartons solides, renforcés et adaptés à chaque type d'affaire — livres, vaisselle,
              vêtements sur cintres, bouteilles ou objets fragiles. Une gamme complète pour préparer
              sereinement votre déménagement à Paris et en Île-de-France, livrée chez vous en 24-48h.
            </p>
          </div>

          {/* 4 specific products */}
          {loading ? (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="aspect-square bg-zinc-100 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="seo-products-grid">
              {[
                "carton-standard-renforce",
                "carton-a-livres",
                "carton-12-bouteilles",
                "carton-75-verres",
              ]
                .map((slug) => products.find((p) => p.slug === slug))
                .filter(Boolean)
                .map((p, idx) => (
                  <ProductCard key={p.id} product={p} index={idx} hideQty goToProduct />
                ))}
            </div>
          )}

          <div className="mt-10 flex justify-center">
            <Link to="/cartons-demenagement" className="btn-outline-blue" data-testid="seo-see-all-cartons">
              Voir tous les cartons <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* BIG DÉMÉNAGEMENT PARTNER — short blue CTA */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container-cdm">
          <BigDemenageurCTA testId="bigdem-cta-button" />
        </div>
      </section>

      {/* PROTECTION & EMBALLAGE — section SEO */}
      <section className="section-pad bg-white border-t border-[#e6edf3]" id="protection">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline">Emballage cartonmove</div>
            <h2 className="mt-3 text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Emballage et protection déménagement
            </h2>
            <p className="mt-5 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Protégez vos meubles, objets fragiles, vaisselle et affaires personnelles avec nos
              solutions d'emballage : papier bulle, film étirable, croisillons pour verres et assiettes,
              papier de calage et protections adaptées au transport.
            </p>
            <p className="mt-3 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Idéal pour éviter les chocs, les rayures et la casse pendant votre déménagement à
              Paris et en Île-de-France.
            </p>
          </div>

          {/* 4 specific protection products */}
          {loading ? (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="aspect-square bg-zinc-100 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="protection-products-grid">
              {[
                "papier-bulle-10m",
                "film-etirable",
                "housse-matelas-2-places",
                "papier-blanc-journal",
              ]
                .map((slug) => products.find((p) => p.slug === slug))
                .filter(Boolean)
                .map((p, idx) => (
                  <ProductCard key={p.id} product={p} index={idx} hideQty goToProduct />
                ))}
            </div>
          )}

          <div className="mt-10 flex justify-center">
            <Link to="/emballage-protection" className="btn-outline-blue" data-testid="see-all-protections">
              Voir toutes les protections <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* PACKS DÉMÉNAGEMENT — fond gris-bleu (comme hero) */}
      <section className="section-pad bg-[#EEF3F7]" id="packs">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline">Packs cartonmove</div>
            <h2 className="mt-3 text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Packs cartons pour studio, appartement et maison
            </h2>
            <p className="mt-5 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Vous ne savez pas combien de cartons commander&nbsp;? Choisissez un pack déménagement
              selon la taille de votre logement. Nos packs sont pensés pour simplifier la préparation
              d'un déménagement à Paris, en appartement, en maison ou en bureau.
            </p>
          </div>

          {/* 4 packs en grille 2×2 */}
          {loading ? (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="aspect-square bg-zinc-100 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="packs-grid">
              {[
                "pack-cartons-demenagement-t1",
                "pack-cartons-demenagement-t2",
                "pack-cartons-demenagement-t3",
                "pack-cartons-demenagement-maison",
              ]
                .map((slug) => products.find((p) => p.slug === slug))
                .filter(Boolean)
                .map((p, idx) => (
                  <ProductCard key={p.id} product={p} index={idx} hideQty goToProduct />
                ))}
            </div>
          )}
        </div>
      </section>

      {/* CARTONS RECONDITIONNÉS — fond blanc */}
      <section className="section-pad bg-white border-t border-[#e6edf3]" id="reconditionnes">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline">Éco-responsable</div>
            <h2 className="mt-3 text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Cartons de déménagement reconditionnés
            </h2>
            <p className="mt-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8] font-semibold">
              Donnez une seconde vie aux cartons de déménagement.
            </p>
            <p className="mt-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Nous proposons des cartons déjà utilisés une fois, contrôlés et remis en vente avec
              <strong className="text-[#0f1d2b]"> jusqu'à -40 % de réduction</strong>. C'est une
              solution économique pour préparer votre déménagement tout en limitant le gaspillage.
            </p>
            <p className="mt-3 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Notre société pense aussi à l'environnement : nous récupérons les cartons auprès de
              nos clients après leur déménagement, puis nous les remettons en circulation lorsqu'ils
              sont encore en bon état.
            </p>
            <p className="mt-3 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Les gains réalisés servent notamment à couvrir les frais de déplacement, de tri et de
              déchetterie pour les cartons qui ne peuvent plus être réutilisés.
            </p>
          </div>

          {/* 2 reconditioned products en grille 2 colonnes mobile + desktop */}
          {loading ? (
            <div className="mt-10 grid grid-cols-2 gap-4 sm:gap-5 max-w-2xl mx-auto">
              {[0, 1].map((i) => (
                <div key={i} className="aspect-square bg-zinc-100 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-2 gap-4 sm:gap-5 max-w-2xl mx-auto" data-testid="reconditionnes-grid">
              {[
                "carton-standard-reconditionne",
                "carton-livres-reconditionne",
              ]
                .map((slug) => products.find((p) => p.slug === slug))
                .filter(Boolean)
                .map((p, idx) => (
                  <ProductCard key={p.id} product={p} index={idx} hideQty goToProduct />
                ))}
            </div>
          )}
        </div>
      </section>

      {/* FERMETURE & ACCESSOIRES */}
      <section className="section-pad bg-[#EEF3F7]" id="accessoires">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline">Accessoires cartonmove</div>
            <h2 className="mt-3 text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Fermeture et accessoires
            </h2>
            <p className="mt-5 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Préparez vos cartons plus facilement avec les accessoires indispensables pour fermer,
              identifier et ouvrir vos emballages en toute sécurité.
            </p>
            <p className="mt-3 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Ruban adhésif, dévidoir, marqueur permanent et cutter : tout le nécessaire pour gagner
              du temps pendant la préparation de votre déménagement à Paris et en Île-de-France.
            </p>
          </div>

          {/* 4 accessories */}
          {loading ? (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="aspect-square bg-zinc-100 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="accessoires-grid">
              {[
                "rouleau-adhesif",
                "devidoir-pistolet-adhesif",
                "marqueur-permanent",
                "cutter",
              ]
                .map((slug) => products.find((p) => p.slug === slug))
                .filter(Boolean)
                .map((p, idx) => (
                  <ProductCard key={p.id} product={p} index={idx} hideQty goToProduct />
                ))}
            </div>
          )}

          <div className="mt-10 flex justify-center">
            <Link to="/fermeture-accessoires" className="btn-outline-blue" data-testid="see-all-accessoires">
              Voir toute la fermeture et accessoires <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* POURQUOI NOUS CHOISIR ? */}
      <section className="section-pad bg-white border-t border-[#e6edf3]" id="pourquoi">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline">Pourquoi cartonmove ?</div>
            <h2 className="mt-3 text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Pourquoi nous choisir ?
            </h2>
            <p className="mt-5 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Nos cartons et emballages sont du matériel professionnel, conçu pour les besoins
              réels du déménagement. Ce sont des produits solides, pratiques et adaptés aux
              particuliers comme aux professionnels.
            </p>
            <p className="mt-3 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Nous sélectionnons des cartons résistants pour protéger vos affaires, faciliter
              le transport et mieux organiser votre déménagement à Paris et en Île-de-France.
            </p>
          </div>

          <div className="mt-10">
            <h3 className="text-lg font-extrabold text-[#0f1d2b] mb-5">Nos avantages :</h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3" data-testid="advantages-list">
              {[
                "Matériel conçu pour les professionnels du déménagement",
                "Cartons solides et adaptés au transport",
                "Produits pour particuliers et entreprises",
                "Solutions pour livres, vêtements, bouteilles et objets fragiles",
                "Cartons reconditionnés disponibles jusqu'à -40 %",
                "Commande simple et rapide",
                "Carton kraft brun 100% recyclable",
              ].map((adv) => (
                <li key={adv} className="flex items-start gap-3 text-[15px] text-[#0f1d2b] leading-relaxed">
                  <CircleCheck className="h-5 w-5 mt-0.5 shrink-0 text-[#237cb5]" />
                  <span>{adv}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============ SEO SECTION : Livraison cartons IDF ============ */}
      <section className="py-16 md:py-24 bg-[#EEF3F7] border-t border-[#cfdbe6]" data-testid="seo-idf-section">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#cfdbe6] text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-5">
              <Truck className="h-3.5 w-3.5" strokeWidth={2.6} />
              Livraison Île-de-France
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b] leading-tight">
              Livraison de cartons de déménagement à&nbsp;Paris et en&nbsp;Île-de-France
            </h2>
            <p className="mt-5 text-[15px] md:text-[16px] text-[#3a4856] leading-relaxed">
              Vous recherchez des cartons de déménagement à Paris ou en région
              parisienne ? <strong className="text-[#0f1d2b]">CartonMove</strong> vous
              accompagne dans la préparation de votre déménagement en vous
              proposant des cartons solides, des kits de déménagement et des
              accessoires d&apos;emballage adaptés à vos besoins.
            </p>
            <p className="mt-4 text-[15px] md:text-[16px] text-[#3a4856] leading-relaxed">
              Nous assurons la livraison de cartons de déménagement dans toute
              l&apos;Île-de-France, directement à votre domicile ou sur votre lieu
              de travail. Vous gagnez ainsi du temps et évitez de transporter
              vous-même des cartons encombrants.
            </p>
          </div>

          {/* Grille départements */}
          <h3 className="mt-12 font-display font-extrabold text-xl md:text-2xl text-[#0f1d2b]">
            Nos zones de livraison en Île-de-France
          </h3>
          <p className="mt-2 text-[14px] text-[#5b6b7a]">
            CartonMove livre les particuliers et les professionnels dans les huit
            départements franciliens :
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                slug: "paris",
                code: "75",
                name: "Paris",
                desc: "Livraison de cartons de déménagement dans tous les arrondissements de Paris, du 1er au 20e arrondissement.",
              },
              {
                slug: "seine-et-marne",
                code: "77",
                name: "Seine-et-Marne",
                desc: "Livraison à Meaux, Melun, Chelles, Fontainebleau, Torcy, Pontault-Combault, Bussy-Saint-Georges et dans les communes voisines.",
              },
              {
                slug: "yvelines",
                code: "78",
                name: "Yvelines",
                desc: "Livraison à Versailles, Saint-Germain-en-Laye, Poissy, Sartrouville, Mantes-la-Jolie, Rambouillet et dans l'ensemble des Yvelines.",
              },
              {
                slug: "essonne",
                code: "91",
                name: "Essonne",
                desc: "Livraison à Évry-Courcouronnes, Massy, Palaiseau, Corbeil-Essonnes, Savigny-sur-Orge et dans tout le département de l'Essonne.",
              },
              {
                slug: "hauts-de-seine",
                code: "92",
                name: "Hauts-de-Seine",
                desc: "Livraison à Boulogne-Billancourt, Nanterre, Courbevoie, Colombes, Asnières-sur-Seine, Levallois-Perret, Antony et dans les villes voisines.",
              },
              {
                slug: "seine-saint-denis",
                code: "93",
                name: "Seine-Saint-Denis",
                desc: "Livraison à Saint-Denis, Montreuil, Aubervilliers, Pantin, Bobigny, Aulnay-sous-Bois, Noisy-le-Grand et dans toute la Seine-Saint-Denis.",
              },
              {
                slug: "val-de-marne",
                code: "94",
                name: "Val-de-Marne",
                desc: "Livraison à Créteil, Vitry-sur-Seine, Ivry-sur-Seine, Vincennes, Maisons-Alfort, Villejuif, Saint-Maur-des-Fossés et dans tout le Val-de-Marne.",
              },
              {
                slug: "val-d-oise",
                code: "95",
                name: "Val-d'Oise",
                desc: "Livraison à Cergy, Argenteuil, Sarcelles, Pontoise, Franconville, Gonesse et dans l'ensemble du Val-d'Oise.",
              },
            ].map(({ slug, code, name, desc }) => (
              <Link
                key={code}
                to={`/cartons-demenagement/${slug}`}
                className="group bg-white rounded-2xl border border-[#cfdbe6] p-5 hover:border-[#237cb5] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(35,124,181,0.35)] transition-all duration-300 block"
                data-testid={`seo-dept-${code}`}
              >
                <div className="flex items-center gap-2.5">
                  <span className="inline-flex items-center justify-center h-9 min-w-[44px] px-2 rounded-lg bg-[#237cb5] text-white text-[13px] font-extrabold tracking-wide">
                    {code}
                  </span>
                  <h4 className="font-display font-extrabold text-[16px] text-[#0f1d2b] leading-tight">
                    Cartons déménagement {name}
                  </h4>
                </div>
                <p className="mt-3 text-[13.5px] text-[#3a4856] leading-relaxed">
                  {desc}
                </p>
                <div className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#237cb5] opacity-0 group-hover:opacity-100 transition-opacity">
                  Voir la page <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
                </div>
              </Link>
            ))}
          </div>

          {/* Closing */}
          <div className="mt-12 max-w-3xl">
            <h3 className="font-display font-extrabold text-xl md:text-2xl text-[#0f1d2b]">
              Commandez vos cartons de déménagement en quelques clics
            </h3>
            <p className="mt-4 text-[15px] md:text-[16px] text-[#3a4856] leading-relaxed">
              Que vous déménagiez un studio, un appartement, une maison ou des
              locaux professionnels, vous pouvez commander la quantité de
              cartons et les accessoires dont vous avez besoin. CartonMove vous
              permet de préparer votre déménagement plus facilement grâce à un
              service pratique de livraison de cartons à Paris et en
              Île-de-France.
            </p>
            <p className="mt-3 text-[15px] md:text-[16px] text-[#3a4856] leading-relaxed">
              Choisissez vos cartons, préparez votre commande et faites-vous
              livrer directement à l&apos;adresse de votre choix.
            </p>
            <Link
              to="/cartons-demenagement"
              className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors [-webkit-tap-highlight-color:transparent]"
              data-testid="seo-idf-cta"
            >
              Voir tous les cartons
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>
        </div>
      </section>

      <StickyMobileMoverCTA />
    </div>
  );
};

export default Home;
